import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BarChart3, Key, ShieldCheck, LogOut, Shield, Building2 } from "lucide-react";
import { useAuth } from "@repo/ping-react-js";
import pingLogoP from "../assets/ping-logo-p.svg";
import { fetchMe } from "../api/me";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, accessToken } = useAuth();
  const [me, setMe] = useState<{
    id: number;
    is_staff: boolean;
    is_org_admin: boolean;
    organization: { short_name: string; name: string } | null;
    self_serve_credential_management_enabled?: boolean;
    organization_credential_management_enabled?: boolean;
  } | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    fetchMe(accessToken)
      .then((res) => setMe(res))
      .catch(() => setMe(null));
  }, [accessToken]);

  const handleLogout = () => {
    window.location.href = `${import.meta.env.VITE_APP_AUTH_URL}/api/fe/v3/logout`;
  };

  const orgShortName = me?.organization?.short_name;

  const isActive = (path: string) => location.pathname === path;
  const isOrgActive = orgShortName
    ? location.pathname === `/org/${orgShortName.toLowerCase()}`
    : false;
  const myCredentialsPath = me?.id != null ? `/user/${me.id}/credentials` : null;
  const isMyCredentialsActive =
    myCredentialsPath != null && location.pathname === myCredentialsPath;

  return (
    <nav className="sidebar">
      <div className="sidebar-top">
        <div className="sidebar-logo">
          <img src={pingLogoP} alt="Ping Intel" />
        </div>
        <div className="sidebar-nav">
          <button
            className={`sidebar-item ${isActive("/usage") ? "active" : ""}`}
            onClick={() => navigate("/usage")}
            title="Usage"
          >
            <BarChart3 size={20} strokeWidth={1.75} />
          </button>
          <button
            className={`sidebar-item ${isActive("/api-keys") ? "active" : ""}`}
            onClick={() => navigate("/api-keys")}
            title="API Keys"
          >
            <Key size={20} strokeWidth={1.75} />
          </button>
          {me && myCredentialsPath && me.self_serve_credential_management_enabled !== false && (
            <button
              className={`sidebar-item ${isMyCredentialsActive ? "active" : ""}`}
              onClick={() => navigate(myCredentialsPath)}
              title="My Credentials"
            >
              <ShieldCheck size={20} strokeWidth={1.75} />
            </button>
          )}
          {(me?.is_org_admin || me?.is_staff) && orgShortName && me?.organization_credential_management_enabled !== false && (
            <button
              className={`sidebar-item ${isOrgActive ? "active" : ""}`}
              onClick={() => navigate(`/org/${orgShortName.toLowerCase()}`)}
              title="Manage Org"
            >
              <Building2 size={20} strokeWidth={1.75} />
            </button>
          )}
          {me?.is_staff && (
            <button
              className={`sidebar-item ${isActive("/org/ping-global") ? "active" : ""}`}
              onClick={() => navigate("/org/ping-global")}
              title="Global Settings"
            >
              <Shield size={20} strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>
      <div className="sidebar-bottom">
        {isLoggedIn && (
          <button
            className="sidebar-item"
            onClick={handleLogout}
            title="Log out"
          >
            <LogOut size={20} strokeWidth={1.75} />
          </button>
        )}
      </div>
    </nav>
  );
};

export default Sidebar;
